import axios from 'axios';

// API基础配置
const BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const SCHOLAR_API_URL = '/testapi/search/search_controller/scholar';

/**
 * 谷歌学术搜索API服务
 */
class ScholarAPI {
  constructor() {
    // 创建axios实例
    this.client = axios.create({
      baseURL: BASE_URL,
      timeout: 30000, // 30秒超时
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // 请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        console.log('Scholar API Request:', config);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.client.interceptors.response.use(
      (response) => {
        console.log('Scholar API Response:', response.data);
        return response;
      },
      (error) => {
        console.error('Scholar API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * 转换前端筛选条件到后端参数
   */
  convertFiltersToParams(filters, query, page = 1) {
    const params = {
      q: query.trim(),
      gl: 'cn', // 地区固定为中国
      page: page
    };

    // 语言参数转换
    if (filters.language) {
      switch (filters.language) {
        case 'zh':
          params.hl = 'zh-cn';
          break;
        case 'en':
          params.hl = 'en';
          break;
        default:
          params.hl = 'zh-cn'; // 默认中文
      }
    } else {
      params.hl = 'zh-cn';
    }

    // 年份范围转换为tbs参数
    if (filters.yearFrom && filters.yearTo) {
      // Google Scholar的年份筛选格式: cd_min:年份,cd_max:年份
      params.tbs = `cd_min:${filters.yearFrom},cd_max:${filters.yearTo}`;
    } else if (filters.yearFrom) {
      params.tbs = `cd_min:${filters.yearFrom}`;
    } else if (filters.yearTo) {
      params.tbs = `cd_max:${filters.yearTo}`;
    }

    // 结果数量 (默认10条)
    params.num = 10;

    // 作者筛选 (如果后端支持)
    if (filters.author && filters.author.trim()) {
      // 将作者名加入搜索查询中
      params.q = `${params.q} author:"${filters.author.trim()}"`;
    }

    // 最小引用数筛选 (前端处理，因为Google Scholar API可能不直接支持)
    if (filters.citationMin && filters.citationMin.trim()) {
      // 可以在返回结果后进行过滤
      params.citationMin = parseInt(filters.citationMin);
    }

    return params;
  }

  /**
   * 转换后端响应数据到前端格式
   */
  convertResponseToFrontend(responseData, citationMin = null) {
    if (!responseData || !responseData.organic) {
      return {
        results: [],
        totalResults: 0,
        currentPage: 1
      };
    }

    let results = responseData.organic.map((item, index) => ({
      id: item.id || `item_${index}`,
      title: item.title || '无标题',
      authors: this.parseAuthors(item.publicationInfo),
      year: item.year || '未知年份',
      venue: this.parseVenue(item.publicationInfo),
      citations: item.citedBy || 0,
      abstract: item.snippet || '暂无摘要',
      url: item.link || '',
      pdfUrl: item.pdfUrl || item.htmlUrl || null,
      rawData: item // 保留原始数据以备后用
    }));

    // 如果设置了最小引用数，进行过滤
    if (citationMin && citationMin > 0) {
      results = results.filter(item => item.citations >= citationMin);
    }

    return {
      results,
      totalResults: responseData.searchParameters?.total_results || (results.length > 0 ? results.length * 50 : 0), // 更合理的估算
      currentPage: responseData.searchParameters?.page || 1,
      searchParameters: responseData.searchParameters,
      filteredByMinCitations: citationMin > 0 ? citationMin : null
    };
  }

  /**
   * 解析作者信息
   */
  parseAuthors(publicationInfo) {
    if (!publicationInfo) return ['未知作者'];
    
    // 实际格式: "DA Hyson - Advances in nutrition, 2011 - Elsevier"
    // 提取第一个 " - " 之前的内容作为作者
    const authorMatch = publicationInfo.match(/^([^-]+?)\s*-/);
    if (authorMatch) {
      const authorsPart = authorMatch[1].trim();
      // 处理多个作者的情况，通常用逗号分隔
      if (authorsPart.includes(',')) {
        return authorsPart.split(',').map(author => author.trim()).filter(author => author);
      }
      return [authorsPart];
    }
    
    return ['未知作者'];
  }

  /**
   * 解析发表期刊/会议信息
   */
  parseVenue(publicationInfo) {
    if (!publicationInfo) return '未知期刊';
    
    // 实际格式: "DA Hyson - Advances in nutrition, 2011 - Elsevier"
    // 提取第一个 " - " 和年份之间的内容
    const venueMatch = publicationInfo.match(/-\s*([^,]+?),\s*\d{4}/);
    if (venueMatch) {
      return venueMatch[1].trim();
    }
    
    // 备选方案：提取第一个 " - " 后面的内容直到年份
    const parts = publicationInfo.split(' - ');
    if (parts.length >= 2) {
      // 移除年份和出版社信息
      const venuePart = parts[1].replace(/,?\s*\d{4}.*$/, '').trim();
      return venuePart || '未知期刊';
    }
    
    return '未知期刊';
  }

  /**
   * 执行搜索
   */
  async search(query, filters = {}, page = 1) {
    if (!query || !query.trim()) {
      throw new Error('搜索关键词不能为空');
    }

    try {
      const params = this.convertFiltersToParams(filters, query, page);
      
      const response = await this.client.post(SCHOLAR_API_URL, params);
      
      // 检查后端返回的状态码
      if (response.data.code === 0) {
        // 参数错误，显示后端返回的具体错误信息
        throw new Error(response.data.message || '查询参数有误');
      } else if (response.data.code === 2) {
        // 服务器内部错误
        throw new Error(response.data.message || '服务器内部错误，请稍后重试');
      } else if (response.data.code !== 1) {
        // 其他未知状态码
        throw new Error(response.data.message || `未知错误 (状态码: ${response.data.code})`);
      }

      // 转换数据格式
      const citationMin = params.citationMin || null;
      return this.convertResponseToFrontend(response.data.responseData, citationMin);
      
    } catch (error) {
      console.error('搜索请求失败:', error);
      
      // 处理不同类型的错误
      if (error.response) {
        // 服务器返回错误响应
        const errorMsg = error.response.data?.message || `服务器错误 (${error.response.status})`;
        throw new Error(errorMsg);
      } else if (error.request) {
        // 网络请求失败
        throw new Error('网络连接失败，请检查网络设置');
      } else {
        // 其他错误
        throw new Error(error.message || '搜索过程中发生未知错误');
      }
    }
  }

  /**
   * 获取论文引用格式 (如果后端支持)
   */
  async getCitation(paperId, format = 'apa') {
    // 这个功能可能需要额外的后端支持
    // 目前返回基本的引用格式
    try {
      // TODO: 如果后端有专门的引用格式API，在这里调用
      return this.generateBasicCitation(paperId, format);
    } catch (error) {
      console.error('获取引用格式失败:', error);
      throw error;
    }
  }

  /**
   * 生成基本引用格式
   */
  generateBasicCitation(paperId, format) {
    // 这里可以实现基本的引用格式生成逻辑
    // 目前返回提示信息
    return `引用格式功能开发中...论文ID: ${paperId}`;
  }
}

// 创建单例实例
const scholarAPI = new ScholarAPI();

export default scholarAPI;